<template>
  <div ref="chartRef" :style="{ width, height }" v-show="showECharts"></div>
  <Empty :style="{ width, height }" v-if="!showECharts" />
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useECharts } from '@/hooks/useECharts'
import type { PropType, Ref } from 'vue'
import calc from '@/utils/calc'
import { colors } from '../dataConfig'
import Empty from './Empty.vue'

const props = defineProps({
  width: {
    type: String as PropType<string>,
    default: '100%'
  },
  height: {
    type: String as PropType<string>,
    default: '260px'
  },
  data: {
    type: Array as PropType<any[]>,
    default: () => {
      return []
    }
  },
  showLabel: {
    type: Boolean,
    default: () => {
      return true
    }
  },
  legendOrient: {
    type: String as PropType<string>,
    default: 'right'
  },
  radius: {
    type: String as PropType<string>,
    default: '90%'
  }
})

const showECharts = ref(false)
const chartRef = ref<HTMLDivElement | null>(null)
const { setOptions } = useECharts(chartRef as Ref<HTMLDivElement>, undefined, false)
watch(
  () => props.data,
  (v) => {
    initChart()
  }
)

function merge(a, b) {
  return Object.entries(b).reduce((o, [k, v]) => {
    o[k] = v && typeof v === 'object' ? merge((o[k] = o[k] || (Array.isArray(v) ? [] : {})), v) : v
    return o
  }, a)
}

const initChart = () => {
  showECharts.value = props.data.length > 0
  const total = props.data.reduce((prev: number, curr: any) => calc.Add(prev, Number(curr.value) || 0), 0)
  const data = props.data.map((item, index) => ({
    ...item,
    label: {
      show: props.showLabel && item.value / total >= 0.1,
      position: 'inside',
      formatter: '{d}%\n{b}',
      color: '#fff',
      fontSize: 12,
      lineHeight: 30,
      emphasis: {
        show: true
      }
    },
    itemStyle: {
      color: index < colors.length ? colors[index] : undefined
    }
  }))

  const option = {
    tooltip: {
      trigger: 'item'
    },
    legend: {
      orient: props.legendOrient === 'right' ? 'vertical' : 'horizontal',
      right: props.legendOrient === 'right' ? 'left' : 'center',
      bottom: props.legendOrient === 'right' ? 'center' : 'bottom',
      top: props.legendOrient === 'right' ? 'middle' : 'bottom',
      icon: 'circle',
      left: '58%',
      
      // padding: [0, 30],
      // selectedMode: false,
      formatter: function (name) {
        let data = option.series[0].data
        const quality = data.find((item) => item.name === name)?.value
        const arr = ['{b|' + name + '}', '{c|' + quality + '}']
        return arr.join(' ')
      },
      textStyle: {
        align: 'right',
        overflow: 'break',
        width: window.innerWidth > 1500 ? 200 : 100,
        rich: {
          b: {
            color: '#6E7B91',
            fontSize: '12px'
          },
          c: {
            fontWeight: 'bold',
            color: '#6E7B91',
            fontSize: '16px'
          }
        }
      }
    },
    series: [
      {
        type: 'pie',
        // percentPrecision: 0,
        radius: props.radius,
        bottom: props.legendOrient === 'right' ? 0 : 30,
        right: props.legendOrient === 'right' ? 150 : 0,
        minAngle: 1,
        // label: {
        //   show: true,
        //   position: 'inside',
        //   formatter: '{d}%\n{b}',
        //   color: '#fff',
        //   fontSize: 14,
        //   lineHeight: 30
        // },
        data: data
        // emphasis: {
        //   disabled: true
        // }
      }
    ]
  }

  setOptions(option)
}

onMounted(() => {
  initChart()
})
</script>
<script lang="ts">
export default { name: 'BoardPieChart' }
</script>
<style scoped></style>
